import { useState, useEffect } from 'react';

export type AccentColor = 'gold' | 'blue' | 'purple' | 'green' | 'rose' | 'orange';

interface AccentColorConfig {
  name: string;
  light: string; // HSL values without hsl()
  dark: string;
  preview: string; // Hex for preview
}

export const accentColors: Record<AccentColor, AccentColorConfig> = {
  gold: {
    name: 'Gold',
    light: '48 100% 45%',
    dark: '48 100% 50%',
    preview: '#FFD400',
  },
  blue: {
    name: 'Blue',
    light: '217 91% 60%',
    dark: '217 91% 65%',
    preview: '#3B82F6',
  },
  purple: {
    name: 'Purple',
    light: '262 83% 58%',
    dark: '262 83% 63%',
    preview: '#8B5CF6',
  },
  green: {
    name: 'Green',
    light: '142 76% 36%',
    dark: '142 76% 42%',
    preview: '#22C55E',
  },
  rose: {
    name: 'Rose',
    light: '346 77% 50%',
    dark: '346 77% 55%',
    preview: '#E11D48',
  },
  orange: {
    name: 'Orange',
    light: '25 95% 53%',
    dark: '25 95% 58%',
    preview: '#F97316',
  },
};

const STORAGE_KEY = 'geniebot-accent-color';

export const useAccentColor = () => {
  const [accentColor, setAccentColorState] = useState<AccentColor>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && stored in accentColors) {
        return stored as AccentColor;
      }
    }
    return 'gold';
  });

  const setAccentColor = (color: AccentColor) => {
    setAccentColorState(color);
    localStorage.setItem(STORAGE_KEY, color);
  };

  useEffect(() => {
    const config = accentColors[accentColor];
    const root = document.documentElement;
    const isDark = root.classList.contains('dark');
    
    const colorValue = isDark ? config.dark : config.light;
    
    // Update CSS variables
    root.style.setProperty('--primary', colorValue);
    root.style.setProperty('--accent', colorValue);
    root.style.setProperty('--ring', colorValue);
    root.style.setProperty('--sidebar-primary', colorValue);
    root.style.setProperty('--sidebar-ring', colorValue);
  }, [accentColor]);

  // Also update when theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const config = accentColors[accentColor];
          const root = document.documentElement;
          const isDark = root.classList.contains('dark');
          const colorValue = isDark ? config.dark : config.light;
          
          root.style.setProperty('--primary', colorValue);
          root.style.setProperty('--accent', colorValue);
          root.style.setProperty('--ring', colorValue);
          root.style.setProperty('--sidebar-primary', colorValue);
          root.style.setProperty('--sidebar-ring', colorValue);
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, [accentColor]);

  return { accentColor, setAccentColor, accentColors };
};
